// ============================================================
// birthday — the optional birthday row on the sign-up form
// ============================================================
//
// Three numeric boxes (year / month / day) become one ISO date string,
// stored as extra_fields.birthday ("1995-04-12"). Pure, no React: the
// renderer and the submissions API's sanitiser share the same rule.

export interface BirthdayParts {
  y: string;
  m: string;
  d: string;
}

export const EMPTY_BIRTHDAY: BirthdayParts = { y: "", m: "", d: "" };

/** Oldest and youngest years a typed birthday may carry. */
const MIN_YEAR = 1900;

/** "YYYY-MM-DD" when the string is a real calendar date between 1900 and
 *  today, else null. Shared by the widget and the submissions API. */
export function validBirthdayIso(value: string, now: Date = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || "").trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < MIN_YEAR || mo < 1 || mo > 12 || d < 1) return null;
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  if (date.getTime() > Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** Reads the three boxes. `empty` = nothing typed; `iso` = a real date;
 *  otherwise the row is incomplete or impossible (`invalid`). */
export function birthdayFromParts(
  parts: BirthdayParts,
  now: Date = new Date()
): { empty: boolean; iso: string | null } {
  const y = parts.y.trim();
  const m = parts.m.trim();
  const d = parts.d.trim();
  if (!y && !m && !d) return { empty: true, iso: null };
  if (y.length !== 4 || !m || !d) return { empty: false, iso: null };
  return {
    empty: false,
    iso: validBirthdayIso(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`, now),
  };
}

/** Digits only, capped at the box's length (4 for the year, 2 otherwise). */
export function birthdayPartInput(part: keyof BirthdayParts, raw: string): string {
  return (raw || "").replace(/\D/g, "").slice(0, part === "y" ? 4 : 2);
}
